import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CheckCircle, XCircle, WarningCircle, Trash, ClockCounterClockwise, Paperclip } from "@phosphor-icons/react";

const statusMeta = {
  sent: { icon: CheckCircle, color: "text-primary", label: "Sent" },
  partial: { icon: WarningCircle, color: "text-chart-5", label: "Partial" },
  failed: { icon: XCircle, color: "text-destructive", label: "Failed" },
};

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    api
      .get("/history")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Failed to load history"))
      .finally(() => setLoading(false));
  }, []);

  const remove = async () => {
    try {
      await api.delete(`/history/${deleteId}`);
      setItems((prev) => prev.filter((i) => i.id !== deleteId));
      toast.success("Entry removed");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleteId(null);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 md:py-14 md:px-12">
      <header className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">History</h1>
        <p className="text-muted-foreground mt-2">Every email you've sent, newest first.</p>
      </header>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-border rounded-md bg-card p-10 text-center" data-testid="history-empty">
          <div className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-5">
            <ClockCounterClockwise size={26} className="text-primary" />
          </div>
          <p className="font-display text-2xl mb-1">Nothing sent yet</p>
          <p className="text-muted-foreground">Your sent emails will show up here.</p>
        </div>
      ) : (
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="history-list">
          {items.map((it, i) => {
            const meta = statusMeta[it.status] || statusMeta.sent;
            const Icon = meta.icon;
            return (
              <motion.div
                key={it.id}
                data-testid={`history-row-${it.id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.03 }}
                className="flex items-start gap-4 px-5 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40 transition-colors duration-200"
              >
                <Icon size={22} weight="fill" className={`${meta.color} mt-0.5 shrink-0`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{it.subject}</p>
                    <span className={`text-xs font-mono ${meta.color}`}>{meta.label}</span>
                  </div>
                  <p className="text-sm text-muted-foreground font-mono truncate mt-1">
                    {it.recipients.slice(0, 3).join(", ")}
                    {it.recipients.length > 3 ? ` +${it.recipients.length - 3} more` : ""}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground font-mono">
                    <span>{fmt(it.sent_at)}</span>
                    <span>·</span>
                    <span>{it.sent_count} delivered</span>
                    {it.failed_count > 0 && <span className="text-destructive">{it.failed_count} failed</span>}
                    {it.attachments && it.attachments.length > 0 && (
                      <span className="flex items-center gap-1" data-testid={`history-attachments-${it.id}`}>
                        <Paperclip size={12} /> {it.attachments.length}
                      </span>
                    )}
                  </div>
                  {it.error && <p className="text-xs text-destructive mt-1">{it.error}</p>}
                </div>
                <button
                  data-testid={`delete-history-${it.id}`}
                  onClick={() => setDeleteId(it.id)}
                  className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                >
                  <Trash size={16} />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>This only removes the log, not the sent email.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-history">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-history" className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
